/**
 * #89 · Vocabulario de la etiqueta individual: estados, veredictos, banderas.
 *
 * Los NÚMEROS de estado no aparecen en este módulo. La API los traduce a
 * nombres (`in_market`, `compromised`) antes de mandarlos, justamente para que
 * el dashboard no tenga que tener una copia de la tabla de
 * `services/shared/states.py`: dos copias de una tabla de estados divergen, y
 * el día que divergen una etiqueta robada se pinta de verde.
 *
 * Sobre «falsificada»: el estado del modelo se llama `compromised` porque una
 * unidad NUESTRA no puede ser falsificada — lo falsificado es una copia que no
 * existe en nuestra base. Lo que sí puede pasar es que un código legítimo haya
 * sido clonado. En la interfaz dice «falsificada», que es como lo llama el
 * equipo de brand protection, y esa traducción vive en el catálogo de textos,
 * que es exactamente donde tiene que vivir una diferencia de vocabulario.
 */
import type { MessageKey } from '@/i18n/messages';
import type { Tone } from '@/ui/Badge';

/** Estado de la unidad, tal como lo nombra la API (`UnitStatusName`). */
export const UNIT_STATES = [
  'created',
  'printed',
  'shipped',
  'received',
  'activated',
  'in_market',
  'verified',
  'voided',
  'stolen',
  'recalled',
  'destroyed',
  'compromised',
] as const;

export type UnitState = (typeof UNIT_STATES)[number];

export function isUnitState(value: string): value is UnitState {
  return (UNIT_STATES as readonly string[]).includes(value);
}

export const UNIT_STATE_LABEL_KEYS: Record<UnitState, MessageKey> = {
  created: 'labels.units.state.created',
  printed: 'labels.units.state.printed',
  shipped: 'labels.units.state.shipped',
  received: 'labels.units.state.received',
  activated: 'labels.units.state.activated',
  in_market: 'labels.units.state.inMarket',
  verified: 'labels.units.state.verified',
  voided: 'labels.units.state.voided',
  stolen: 'labels.units.state.stolen',
  recalled: 'labels.units.state.recalled',
  destroyed: 'labels.units.state.destroyed',
  compromised: 'labels.units.state.compromised',
};

/**
 * El color es estado, no decoración. Verde sólo lo que autentica; rojo lo que
 * exige que alguien actúe.
 *
 * `verified` (el estado 6) va en verde porque autentica; `voided` y
 * `destroyed` van en neutro porque son bajas administrativas, no incidentes.
 */
export const UNIT_STATE_TONES: Record<UnitState, Tone> = {
  created: 'neutral',
  printed: 'neutral',
  shipped: 'neutral',
  received: 'neutral',
  activated: 'verified',
  in_market: 'verified',
  verified: 'verified',
  voided: 'neutral',
  stolen: 'critical',
  recalled: 'critical',
  destroyed: 'neutral',
  compromised: 'critical',
};

/** Veredicto que devuelve la API para la marca (`LabelDetailOut.verdict`). */
export const VERDICT_LABEL_KEYS: Record<string, MessageKey> = {
  genuine: 'labels.units.verdict.genuine',
  already_seen: 'labels.units.verdict.alreadySeen',
  unverified: 'labels.units.verdict.unverified',
  stolen: 'labels.units.verdict.stolen',
  compromised: 'labels.units.verdict.compromised',
  recalled: 'labels.units.verdict.recalled',
};

export const VERDICT_TONES: Record<string, Tone> = {
  genuine: 'verified',
  already_seen: 'suspect',
  unverified: 'suspect',
  stolen: 'critical',
  compromised: 'critical',
  recalled: 'critical',
};

/**
 * `null` si el veredicto no está en el catálogo: se muestra la cadena pelada.
 *
 * No es defensivo por las dudas — es el mismo criterio que `unitStatusKey` en
 * el módulo de activación. Un veredicto nuevo del servidor tiene que aparecer
 * como texto crudo y no romper la pantalla ni, peor, pintarse como si fuera
 * otro que sí conocemos.
 */
export function verdictKey(verdict: string): MessageKey | null {
  return VERDICT_LABEL_KEYS[verdict] ?? null;
}

export function verdictTone(verdict: string): Tone {
  return VERDICT_TONES[verdict] ?? 'neutral';
}

/** Ídem para el estado: `null` si el nombre no está en el catálogo. */
export function unitStateKey(state: string): MessageKey | null {
  return isUnitState(state) ? UNIT_STATE_LABEL_KEYS[state] : null;
}

/** Firma de `t`, para las funciones puras que traducen sin ser componentes. */
export type Translate = (key: MessageKey, values?: Record<string, string | number>) => string;

/**
 * El nombre traducido de un estado, o el crudo si no está en el catálogo.
 *
 * Vive acá y no junto a las insignias porque un archivo `.tsx` exporta
 * componentes y nada más: mezclarlos rompe el hot reload y el linter lo marca.
 */
export function stateLabel(state: string, t: Translate): string {
  const key = unitStateKey(state);
  return key === null ? state : t(key);
}

export function unitStateTone(state: string): Tone {
  return isUnitState(state) ? UNIT_STATE_TONES[state] : 'neutral';
}

/**
 * Las banderas, en el orden de la maqueta del socio. Cada una lleva la clave
 * del texto que EXPLICA de dónde sale — un casillero marcado sin explicación
 * es un casillero con el que alguien decide un caso a ciegas.
 *
 * `critical: true` pinta la bandera de rojo cuando está encendida. Las
 * neutras (escaneada, activa) no cambian de color: que una etiqueta se haya
 * escaneado no es una alarma.
 */
export interface FlagSpec {
  key:
    | 'scanned'
    | 'expired'
    | 'authenticates'
    | 'suspicious'
    | 'compromised'
    | 'stolen'
    | 'recalled'
    | 'voided'
    | 'terminal'
    | 'reappeared';
  labelKey: MessageKey;
  sourceKey: MessageKey;
  critical: boolean;
}

export const FLAG_SPECS: readonly FlagSpec[] = [
  {
    key: 'scanned',
    labelKey: 'labels.units.flag.scanned',
    sourceKey: 'labels.units.flag.scanned.source',
    critical: false,
  },
  {
    key: 'expired',
    labelKey: 'labels.units.flag.expired',
    sourceKey: 'labels.units.flag.expired.source',
    critical: false,
  },
  {
    key: 'authenticates',
    labelKey: 'labels.units.flag.authenticates',
    sourceKey: 'labels.units.flag.authenticates.source',
    critical: false,
  },
  {
    key: 'suspicious',
    labelKey: 'labels.units.flag.suspicious',
    sourceKey: 'labels.units.flag.suspicious.source',
    critical: true,
  },
  {
    key: 'compromised',
    labelKey: 'labels.units.flag.compromised',
    sourceKey: 'labels.units.flag.compromised.source',
    critical: true,
  },
  {
    key: 'stolen',
    labelKey: 'labels.units.flag.stolen',
    sourceKey: 'labels.units.flag.stolen.source',
    critical: true,
  },
  {
    key: 'recalled',
    labelKey: 'labels.units.flag.recalled',
    sourceKey: 'labels.units.flag.recalled.source',
    critical: true,
  },
  {
    key: 'voided',
    labelKey: 'labels.units.flag.voided',
    sourceKey: 'labels.units.flag.voided.source',
    critical: false,
  },
  {
    key: 'terminal',
    labelKey: 'labels.units.flag.terminal',
    sourceKey: 'labels.units.flag.terminal.source',
    critical: false,
  },
  {
    key: 'reappeared',
    labelKey: 'labels.units.flag.reappeared',
    sourceKey: 'labels.units.flag.reappeared.source',
    critical: true,
  },
];

/** Las tres acciones, tal como las nombra la API (`LabelAction`). */
export const LABEL_ACTIONS = ['stolen', 'compromised', 'void'] as const;

export type LabelActionName = (typeof LABEL_ACTIONS)[number];

export const ACTION_LABEL_KEYS: Record<LabelActionName, MessageKey> = {
  stolen: 'labels.units.action.stolen',
  compromised: 'labels.units.action.compromised',
  void: 'labels.units.action.void',
};

/** Qué le pasa a la etiqueta. Se muestra ANTES de confirmar, no después. */
export const ACTION_EFFECT_KEYS: Record<LabelActionName, MessageKey> = {
  stolen: 'labels.units.action.stolen.effect',
  compromised: 'labels.units.action.compromised.effect',
  void: 'labels.units.action.void.effect',
};

/**
 * Mínimo del motivo. Es el mismo que valida `LabelActionIn.reason` y el mismo
 * que exige el CHECK de `unit_status_change`: si los tres no coinciden, el
 * formulario deja mandar algo que la base rechaza con un 500.
 */
export const MIN_REASON = 8;

/** Los ámbitos del listado. `all` es la marca entera (#159) y es el default. */
export const SCOPES = ['all', 'batch', 'roll', 'code'] as const;

export type Scope = (typeof SCOPES)[number];

export function isScope(value: string): value is Scope {
  return (SCOPES as readonly string[]).includes(value);
}
